import sanitizeHtml from 'sanitize-html';

const sanitizeOptions = {
    allowedTags: [
        // Text containers
        'p', 'div', 'pre', 'code',
        // Headers
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        // Text formatting
        'b', 'i', 'em', 'strong', 'span',
        // Lists
        'ul', 'ol', 'li',
        // Line breaks and spacing
        'br', 'hr',
        // Links
        'a',
        // Tables
        'table', 'thead', 'tbody', 'tr', 'td', 'th'
    ],
    allowedAttributes: {
        'a': ['href']
    },
    disallowedTagsMode: 'escape' as const,
    parser: {
        decodeEntities: true
    }
};

const escapeHtml = (unsafe: string): string => {
    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
};

const renderNode = (
    node: unknown,
    currentPath: string[],
    pathsAndTypes: Record<string, string>
): string => {
    // For leaf nodes, check type and render accordingly
    const fullPath = currentPath.join('.');
    const type = pathsAndTypes[fullPath] ?? typeof node;
    const optionalType = type.replace(/!/g, '');

    switch (optionalType) {
        case 'String':
        case 'string':
            return `<p>${escapeHtml(String(node))}</p>`;
        case 'SectionTitle':
            return `<h1>${escapeHtml(String(node))}</h1>`;
        case 'MarkdownString':
            return `<pre style="white-space: pre-wrap;">${escapeHtml(String(node))}</pre>`;
        case 'HtmlString':
            const sanitized = sanitizeHtml(String(node), sanitizeOptions);
            // Ensure output is wrapped in p tag and single quotes are converted to HTML entities
            return sanitized.replace(/'/g, '&#39;');
        case 'ImageUrl':
            return `<img src="${escapeHtml(String(node))}" alt="Profile picture" style="max-width: 200px; border-radius: 8px; float: right; margin-left: 16px;" />`;
        case '[Link]': // Links on the contact page
            return `<ul>
                ${(node as Record<string, string>[]).map(link => {
                    const label = link.label ?? link.url ?? 'undefined';
                    return link.url
                        ? `<li><a href="${escapeHtml(link.url)}">${escapeHtml(label)}</a></li>`
                        : `<li>${escapeHtml(label)}</li>`;
                }).join('')}
            </ul>`;
        case 'Link': // Company links on the experience page
            const link = node as Record<string, string>;
            const url = link.url;
            const label = link.label ?? url ?? 'undefined';
            return url
                ? `<h3 class="company"><a href="${escapeHtml(url)}">${escapeHtml(label)}</a></h3>` 
                : `<h3 class="company">${escapeHtml(label)}</h3>`;
        case 'Period':
            const period = node as Record<string, string>;
            const start = period.start;
            const end = period.end;
            if (start && end) {
                return `<p class="period">${escapeHtml(period.start)} &rarr; ${escapeHtml(period.end)}</p>`;
            }
            if (start) {
                return `<p class="period">Since ${escapeHtml(period.start)}</p>`;
            }
            if (end) {
                return `<p class="period">Until ${escapeHtml(period.end)}</p>`;
            }
    }

    // If node is an object, recursively render its properties
    if (node && typeof node === 'object') {
        return Object.entries(node as Record<string, unknown>)
            .map(([key, value]) => {
                const newPath = [...currentPath, key];
                return renderNode(value, newPath, pathsAndTypes);
            })
            .join('');
    }

    return `<div>Unknown type: ${type}</div>`;
};

interface InputResult {
    data: unknown;
    errors?: { message: string }[];
    extensions: {
        pathsAndTypes: Record<string, string>;
    };
}

export const renderResult = (input: InputResult): string => {
    return (input.data ? renderNode(input.data, [], input.extensions.pathsAndTypes) : '')
        + (input.errors ? input.errors.map(error => `<p class="error"><span>&#9888;</span> ${escapeHtml(error.message)}</p>`).join('') : '');
};
